<template>
  <div class="bg-background">
    <div
      class="sidebar-wrapper no-print group/sidebar flex h-screen flex-col overflow-hidden"
      :class="sidebarWrapperClasses">
      <div class="sidebar-head relative min-h-20" :class="sidebarHeadClasses">
        <!-- Logo Open Sidebar -->
        <div
          class="absolute left-0 top-0 flex h-full w-full items-center justify-between gap-8 py-6 pl-10 pr-6 transition"
          :class="logoOpenClasses"
          @click="isCollapse = !isCollapse">
          <LogoText class="block h-10 w-auto opacity-100 transition" />

          <PanelLeftClose
            :size="24"
            class="cursor-pointer text-[var(--sm-sidebar-toggle-icon-color)] opacity-0 transition hover:text-white group-hover/sidebar:opacity-100" />
        </div>

        <!-- Logo Close Sidebar -->
        <div
          class="group/logo absolute left-0 top-0 flex h-full w-full cursor-pointer items-center justify-between gap-8 py-6 pl-6 pr-6 transition"
          :class="logoCloseClasses"
          @click="isCollapse = !isCollapse">
          <LogoIcon
            class="block h-full !w-full max-w-40 text-white opacity-100 transition delay-100 group-hover/logo:opacity-0"
            style="width: 140px" />

          <div
            v-show="!collapseTransition"
            class="absolute left-0 top-0 flex h-full w-full items-center justify-center opacity-0 transition delay-100 group-hover/logo:opacity-100">
            <PanelLeftOpen
              :size="24"
              class="h-12 w-12 cursor-pointer rounded p-2 text-[var(--sm-sidebar-toggle-icon-color)] hover:bg-[var(--sm-sidebar-link-bg-hover)] hover:text-white" />
          </div>
        </div>
      </div>

      <nav
        ref="navContainer"
        class="flex-shrink-1 relative flex flex-col overflow-y-auto overflow-x-hidden transition">
        <div
          class="animated-bg flex w-full justify-end"
          :style="{
            top: activeTabPosition.top + 'px',
            height: activeTabPosition.height + 'px',
          }">
          <div class="h-full w-1 bg-white"></div>
        </div>

        <RouterLink
          v-for="item in menu"
          :key="item.name"
          :to="item.route"
          class="router-link flex items-center gap-3 py-4 pl-10 text-[var(--sm-sidebar-link-color)]"
          :class="{ collapsed: isCollapse }">
          <component
            :is="item.icon"
            :size="20"
            class="shrink-0 text-[var(--sm-sidebar-link-icon-color)]"
            :class="item.iconClass" />
          <span
            class="whitespace-nowrap transition-all duration-300 ease-in-out"
            :class="{ 'opacity-0': isCollapse, 'opacity-100': !isCollapse }">
            {{ item.displayName }}
          </span>
        </RouterLink>
      </nav>

      <SmDropdown
        v-if="sessionStore.currentUser && companyData?.name"
        trigger="click"
        type="panel"
        class="mt-auto">
        <template #trigger>
          <div
            class="sidebar-footer px-3 pb-3 pt-2 transition"
            :class="{ '!pl-3': isCollapse }">
            <div
              class="flex w-full cursor-pointer items-center gap-4 rounded p-4 text-white transition hover:bg-[var(--sm-sidebar-link-bg-active)]"
              :class="{ 'pl-[18px]': isCollapse }">
              <SmAvatar
                background-color="white"
                size="small"
                :name="sessionStore.currentUser?.display_name" />
              <div
                class="opacity-100 transition"
                :class="{ 'hidden opacity-0': isCollapse }">
                <TextMultiline
                  :text="sessionStore.currentUser?.display_name"
                  class="font-semibold" />
                <TextMultiline
                  :text="sessionStore.currentCompany?.name"
                  class="text-sm" />
              </div>

              <ChevronsUpDown
                :size="22"
                class="ml-auto opacity-100 transition"
                :class="{ 'hidden !opacity-0': isCollapse }" />
            </div>
          </div>
        </template>

        <!-- User Settings -->
        <SmDropdownItem
          v-if="sessionStore.status === 'LOGGED_IN'"
          :icon="Settings"
          size="medium"
          class="stroke-contrast"
          @click="openDialog('settings')">
          {{ i18n.t('accountSettings') }}
        </SmDropdownItem>

        <!-- Switch Company -->
        <SmDropdownItem
          v-if="sessionStore.status === 'LOGGED_IN'"
          :icon="BriefcaseBusiness"
          size="medium"
          @click="sessionStore.switchCompany()">
          {{ i18n.t('switchCompany') }}
        </SmDropdownItem>

        <hr class="my-3 min-w-72 dark:border-gray-700" />

        <!-- Logout -->
        <SmDropdownItem
          v-if="sessionStore.status === 'LOGGED_IN'"
          :icon="LogOut"
          size="medium"
          hover-color="var(--sm-red)"
          @click="sessionStore.logout()">
          {{ i18n.t('headerMenu.settings.logout') }}
        </SmDropdownItem>
      </SmDropdown>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useWindowSize } from '@vueuse/core'

  import { useI18n } from 'vue-i18n'
  import {
    PanelLeftClose,
    PanelLeftOpen,
    ChevronsUpDown,
    House,
    LayoutGrid,
    File,
    Folder,
    Compass,
    Workflow,
    Users,
    User,
    Settings,
    BriefcaseBusiness,
    LogOut,
  } from 'lucide-vue-next'

  import LogoIcon from '@/assets/img/LogoIcon.vue'
  import LogoText from '@/assets/img/LogoText.vue'
  import {
    CompanyData,
    CompanyManagementService,
    UserProfileService,
  } from '@/client'
  import { useRoute } from 'vue-router'
  import { useSessionStore } from '@/stores/sessionStore'
  import { openDialog } from '@/utils/dialogManager'

  const i18n = useI18n()
  const sessionStore = useSessionStore()

  type MenuItem = {
    displayName: string
    name: string
    route: string
    icon: Component
    iconClass?: string
    disabled: boolean
    children?: MenuItem[]
  }

  const menu = computed((): MenuItem[] => [
    {
      displayName: i18n.t('sidebar.dashboard'),
      name: 'Dashboard',
      route: '/dashboard',
      icon: House,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.applications'),
      name: 'Applications',
      route: '/applications',
      icon: LayoutGrid,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.accounts'),
      name: 'Accounts',
      route: '/accounts',
      icon: File,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.users'),
      route: '/users',
      name: 'Users',
      icon: User,
      disabled: false,
    },
    {
      displayName: i18n.t('groups'),
      name: 'Groups',
      route: '/groups',
      icon: Users,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.documents'),
      name: 'Documents',
      route: '/documents',
      icon: Folder,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.discoveryTools'),
      name: 'Discovery-Tools',
      route: '/discovery',
      icon: Compass,
      disabled: false,
    },
    {
      displayName: i18n.t('sidebar.automations'),
      name: 'Automation',
      route: '/automations',
      icon: Workflow,
      disabled: false,
    },
  ])

  // ---------------------------------------------------------------------------
  // Indicator
  // ---------------------------------------------------------------------------

  const route = useRoute()
  const navContainer = ref<HTMLElement | null>(null)
  const collapseTransition = ref<boolean>(false)

  watch(
    () => route.path,
    () => {
      nextTick(() => {
        setActiveItemPosition()
      })
    },
    {
      immediate: true,
      deep: true,
    }
  )

  interface ActiveTabPosition {
    top: number
    height: number
  }
  const activeTabPosition = ref<ActiveTabPosition>({
    top: 0,
    height: 45,
  })

  function setActiveItemPosition() {
    const activeItemElememt = navContainer.value?.querySelector(
      '.router-link-active'
    ) as HTMLElement | null

    if (!activeItemElememt) return
    const box = activeItemElememt.getBoundingClientRect()

    const activeItemParentElement = activeItemElememt.parentNode as HTMLElement
    const boxParent = activeItemParentElement.getBoundingClientRect()

    activeTabPosition.value.top = box.y - boxParent.y
    activeTabPosition.value.height = box.height
  }

  // ---------------------------------------------------------------------------
  // Collapse Sidebar
  // ---------------------------------------------------------------------------

  const { width } = useWindowSize()

  const sidebarWrapperClasses = computed(() =>
    isCollapse.value ? 'w-28' : 'w-80'
  )
  const sidebarHeadClasses = computed(() =>
    isCollapse.value ? 'py-3 px-6' : 'py-6 pl-10 pr-6'
  )
  const logoOpenClasses = computed(() =>
    isCollapse.value ? 'opacity-0 delay-0' : 'opacity-100  delay-100'
  )
  const logoCloseClasses = computed(() =>
    isCollapse.value
      ? 'pointer-events-auto opacity-100 delay-100'
      : ' opacity-0  delay-0 pointer-events-none'
  )

  const isCollapse = ref<boolean>(
    sessionStorage.getItem('sidebar-collapse') !== null
      ? sessionStorage.getItem('sidebar-collapse') === 'true'
      : false
  )
  const collapsedByWatch = ref<boolean>(false)

  watch(
    () => width.value,
    () => {
      if (width.value <= 1400) {
        isCollapse.value = true
      } else isCollapse.value = false

      collapsedByWatch.value = true
    }
  )

  watch(isCollapse, async (newVal) => {
    collapseTransition.value = true

    try {
      await UserProfileService.updateUserProfileUserPatch({
        requestBody: {
          preferences: { sidebar_collapse: newVal },
        },
        xTransactionId: null,
      })
    } catch (error) {
      console.error('Failed to update user preferences:', error)
    } finally {
      sessionStorage.setItem('sidebar-collapse', String(newVal))
      document.dispatchEvent(
        new CustomEvent('sidebar-collapse-state-changed', {
          detail: { state: String(newVal) },
        })
      )
    }

    collapsedByWatch.value = false
    setTimeout(() => (collapseTransition.value = false), 900)
  })

  const companyData = ref<CompanyData | null>(null)

  onMounted(() => {
    CompanyManagementService.getCompanyInfoApiV1CompanyInfoGet({}).then(
      (res) => {
        companyData.value = res
      }
    )
  })
</script>

<style lang="scss" scoped>
  .sidebar-wrapper {
    position: sticky !important;
    background-color: var(--sm-sidebar);
    box-shadow: var(--shadow);
    height: 100%;
    transition: all 0.2s ease-in-out;
  }

  .sidebar-head {
    min-height: var(--layout-size-header);
  }

  .animated-bg {
    position: absolute;
    z-index: -1;
    transition:
      top 0.2s ease,
      height 0.2s ease;
    background-color: var(--sm-sidebar-link-bg-active);
  }

  .router-link {
    transition: background-color 0.15s ease;
  }

  .router-link:hover:not(.router-link-active) {
    background-color: var(--sm-sidebar-link-bg-hover);
  }
</style>
